import React from "react";
import { ArrowRight } from "lucide-react";
import { LinkComponent } from "../../../types";
import { ProductCollectionFilter, applyProductCollectionFilter } from "../ProductSlider/productCollection.shared";
import { ProductItemTile, ProductItemTileItem } from "./ProductItemTiles";
import ProductEmptyState from "../ProductEmptyState/ProductEmptyState";
import { baseStyles, neutralStyles } from "./productItemTilesSlider.baseStyles";
import { productItemTilesTokens, ProductItemTilesStyle } from "./productItemTiles.tokens";
import { getBadgeText, getCtaText, isValidCtaUrl } from "./productItemTiles.helpers";

export type { ProductItemTilesStyle } from "./productItemTiles.tokens";

export type ProductItemTilesSliderProps = {
    items: ProductItemTileItem[];
    Link: LinkComponent;
    title?: string;
    eyebrow?: string;
    description?: string;
    tileClasses?: string;
    filter?: ProductCollectionFilter;
    emptyTitle?: string;
    emptyDescription?: string;
    style?: ProductItemTilesStyle;
    ctaText?: string;
    ctaUrl?: string;
};

export default function ProductItemTilesSlider({
    items,
    Link,
    title,
    eyebrow,
    description,
    tileClasses,
    filter,
    emptyTitle,
    emptyDescription,
    style,
    ctaText,
    ctaUrl,
}: ProductItemTilesSliderProps) {
    const filteredItems = applyProductCollectionFilter(items, filter);
    const token = style ? productItemTilesTokens[style] : undefined;
    const safeCtaUrl = isValidCtaUrl(ctaUrl) ? ctaUrl?.trim() : "";
    const shouldShowCta = !!safeCtaUrl;
    const resolvedCtaText = getCtaText(ctaText, title);
    const showScrollHint = filteredItems.length > 1;

    return (
        <section style={{ ...baseStyles.section, ...(token?.section ?? {}) }}>
            <style>{`
        @keyframes productItemTilesSliderHint {
          0%, 100% { transform: translateY(-50%) translateX(0); opacity: 0.72; }
          50% { transform: translateY(-50%) translateX(6px); opacity: 0.96; }
        }

        .product-item-tiles-cta-link {
          display: inline-flex;
          text-decoration: none;
        }

        @media (max-width: 640px) {
          .product-item-tiles-slider-hint-text { display: none; }
        }
      `}</style>

            {token ? <div aria-hidden="true" style={token.accentLine} /> : null}

            {eyebrow || title || description ? (
                <div style={baseStyles.header}>
                    {eyebrow ? <p style={{ ...baseStyles.eyebrow, ...(token?.eyebrow ?? {}) }}>{eyebrow}</p> : null}

                    {title || token ? (
                        <div style={baseStyles.titleRow}>
                            {title ? <h2 style={{ ...baseStyles.title, ...(token?.title ?? {}) }}>{title}</h2> : null}
                            {token ? <span style={token.badge}>{getBadgeText(style)}</span> : null}
                        </div>
                    ) : null}

                    {description ? <p style={{ ...baseStyles.description, ...(token?.description ?? {}) }}>{description}</p> : null}
                </div>
            ) : null}

            {filteredItems.length === 0 ? (
                <ProductEmptyState title={emptyTitle} description={emptyDescription} />
            ) : (
                <div style={baseStyles.scrollArea}>
                    <div style={baseStyles.scrollWrap}>
                        <div style={baseStyles.track}>
                            {filteredItems.map((item) => (
                                <div key={item.id} style={baseStyles.itemWrap} className={tileClasses ?? ""}>
                                    <ProductItemTile item={item} Link={Link} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {showScrollHint ? (
                        <>
                            <div aria-hidden="true" style={baseStyles.edgeFade} />
                            <div aria-hidden="true" style={token?.scrollHint ?? neutralStyles.scrollHint}>
                                <span
                                    className="product-item-tiles-slider-hint-text"
                                    style={token?.scrollHintText ?? neutralStyles.scrollHintText}
                                >
                                    Scroll
                                </span>
                                <span style={token?.scrollHintIcon ?? neutralStyles.scrollHintIcon}>
                                    <ArrowRight size={15} strokeWidth={3} />
                                </span>
                            </div>
                        </>
                    ) : null}
                </div>
            )}

            {shouldShowCta ? (
                <div style={token?.ctaWrap ?? neutralStyles.ctaWrap}>
                    <Link href={safeCtaUrl} className="product-item-tiles-cta-link">
                        <span style={token?.ctaButton ?? neutralStyles.ctaButton}>
                            {resolvedCtaText}
                            <ArrowRight size={16} strokeWidth={2.5} />
                        </span>
                    </Link>
                </div>
            ) : null}
        </section>
    );
}
